import {createSelector, Selector} from "@reduxjs/toolkit";
import {getUseQueryResultData} from "../../util/selector-util";

type Transform<TIn, TOut> = (value: TIn) => TOut;
type Filter<T, P> = (value: T, props: P) => boolean;
type Sorter<T, P> = (a: T, b: T, props: P) => number;

// "Input selector" type (what createSelector expects)
type InputSelector<S = any, R = any> = (state: S, ...args: any[]) => R;

type EntitySelectorHelpers<TModel, TEntity, TProps> = {
  selectModels: (values?: TModel[], props?: TProps) => TModel[];
  transform: Transform<TModel, TEntity>;
  selectEntities: (values?: TModel[], props?: TProps) => TEntity[];
};

export function makeEntitySelectors<TModel, TEntity, TProps extends object>(cfg: {
  defaultProps: TProps;
  transform: Transform<TModel, TEntity>;
  filter?: Filter<TModel, TProps>;
  sort?: Sorter<TModel, TProps>;

  extend?: (helpers: EntitySelectorHelpers<TModel, TEntity, TProps>) => Record<string, any>;
}) {
  const selectModels = (values?: TModel[], props?: TProps): TModel[] => {
    const p = {...cfg.defaultProps, ...(props ?? {})} as TProps;
    const base = values ?? [];

    const filtered = cfg.filter ? base.filter((v) => cfg.filter!(v, p)) : base.slice();

    if (cfg.sort) {
      filtered.sort((a, b) => cfg.sort!(a, b, p));
    }

    return filtered;
  };

  const transform = cfg.transform;

  const selectEntities = (values?: TModel[], props?: TProps): TEntity[] =>
    selectModels(values, props).map(transform);

  const listSelector = (props?: TProps) =>
    createSelector([getUseQueryResultData<TModel[]>], (data) =>
      selectEntities(data ?? [], props)
    );

  const listSelectorWithParams = <
    const TInputs extends readonly InputSelector<any, any>[],
    SProps extends TProps | undefined,
  >(
    extraInputs: TInputs,
    toProps: (...args: { [I in keyof TInputs]: TInputs[I] extends InputSelector<any, infer R> ? R : never }) => SProps
  ) =>
    createSelector(
      [getUseQueryResultData<TModel[]>, ...extraInputs] as const,
      (data, ...extra) => {
        const propsFromParams = toProps(...extra as any);
        return selectEntities(data ?? [], propsFromParams);
      }
    )

  const singleSelector = () =>
    createSelector([getUseQueryResultData<TModel>], (data) =>
      data ? transform(data) : undefined
    );

  const helpers: EntitySelectorHelpers<TModel, TEntity, TProps> = {
    selectModels,
    transform,
    selectEntities,
  };

  return {
    selectModels,
    selectEntities,
    transform,

    listSelector,
    listSelectorWithParams,
    singleSelector,

    ...(cfg.extend ? cfg.extend(helpers) : {}),
  };
}
